import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { NOTE, TASK } from 'customer-data-objects/engagement/EngagementTypes';
import { LOGGED_EMAIL } from 'customer-data-communicator/schema/interactions/InteractionTypes';
import { ShinySkeletonCircle, ShinySkeletonText, ShinySkeletonTextBody } from './helpers/skeletonHelpers';
import { SKELETON_TEXT_HEIGHT } from './helpers/SkeletonText';
import { useTimelineCollapsedContext, isLoading } from 'crm-events-data';
import { ScreenReaderOnly } from 'foundations-components/transitional/components/ScreenReaderOnly';
import AccessibleEventHeading from '../eventStructure/AccessibleEventHeading';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isEmailEngagement } from 'crm-events-data';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CARD_PADDING_HEIGHT = 32; // from p-all-4
const TEXT_LINE_HEIGHT = SKELETON_TEXT_HEIGHT + 8;
const HEADER_HEIGHT = 28;
const SUBHEADER_HEIGHT = 24;
function CompactEngagementSkeleton({
  engagementType,
  heightOverride,
  shouldUseMarginBottom,
  shouldShowBorder
}) {
  const isCollapsedByDefault = useTimelineCollapsedContext();
  // if the event is a non-engagement, engagementType is undefined
  const shouldDisplayAsExpanded = !isLoading(isCollapsedByDefault) && !isCollapsedByDefault && engagementType;
  const shouldShowMoreBodyTextLines = engagementType === NOTE || isEmailEngagement(engagementType) || engagementType === LOGGED_EMAIL;
  const hasAdditionalContent = engagementType === TASK || isEmailEngagement(engagementType);
  const numberOfBodyTextLines = useMemo(() => {
    if (heightOverride) {
      const headerTextLineHeight = HEADER_HEIGHT + (hasAdditionalContent ? SUBHEADER_HEIGHT : 0);
      const remainingCardHeight = heightOverride - CARD_PADDING_HEIGHT - headerTextLineHeight;
      return Math.floor(remainingCardHeight / TEXT_LINE_HEIGHT);
    }
    if (shouldShowMoreBodyTextLines) {
      return shouldDisplayAsExpanded ? 8 : 2;
    }
    return shouldDisplayAsExpanded ? 6 : engagementType === TASK ? 0 : 1;
  }, [engagementType, hasAdditionalContent, heightOverride, shouldDisplayAsExpanded, shouldShowMoreBodyTextLines]);
  const renderedBody = useMemo(() => Array.from({
    length: numberOfBodyTextLines
  }, (_, index) => /*#__PURE__*/_jsx(Flex, {
    marginInlineStart: "500",
    alignItems: "center",
    blockSize: `${TEXT_LINE_HEIGHT}px`,
    children: /*#__PURE__*/_jsx(ShinySkeletonTextBody, {
      width: "calc(100% - 20px)",
      "data-test-id": "text-line-skeleton"
    })
  }, index)), [numberOfBodyTextLines]);
  const renderedAdditionalContent = useMemo(() => {
    if (engagementType === TASK) {
      return /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        marginBlockEnd: isCollapsedByDefault !== false ? '100' : undefined,
        paddingBlock: "100",
        paddingInlineStart: "500",
        "data-test-id": "task-body-skeleton",
        gap: "300",
        children: [/*#__PURE__*/_jsx(ShinySkeletonCircle, {
          size: 20
        }), /*#__PURE__*/_jsx(ShinySkeletonText, {
          width: 200
        })]
      });
    }
    if (isEmailEngagement(engagementType)) {
      return /*#__PURE__*/_jsx(Flex, {
        paddingBlock: "100",
        paddingInlineStart: "500",
        children: /*#__PURE__*/_jsx(ShinySkeletonText, {
          width: 175,
          "data-test-id": "email-body-skeleton"
        })
      });
    }
    return null;
  }, [engagementType, isCollapsedByDefault]);
  return /*#__PURE__*/_jsx("article", {
    tabIndex: -1,
    "aria-labelledby": "skeleton-card",
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      "data-selenium-test": "timeline-card-skeleton",
      "data-test-id": "engagement-skeleton",
      height: heightOverride,
      marginBlockEnd: shouldUseMarginBottom ? '200' : '0',
      id: "skeleton-card",
      padding: "400",
      borderWidth: shouldShowBorder ? undefined : '0',
      children: [/*#__PURE__*/_jsx(ScreenReaderOnly, {
        children: /*#__PURE__*/_jsx(AccessibleEventHeading, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "components.accessibility.skeletonEvent"
          })
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        marginInlineStart: "500",
        paddingBlockStart: "100",
        paddingBlockEnd: "200",
        children: [/*#__PURE__*/_jsx(ShinySkeletonText, {
          width: isEmailEngagement(engagementType) ? 350 : 150,
          "data-test-id": "left-header-skeleton"
        }), /*#__PURE__*/_jsx(ShinySkeletonText, {
          width: 182,
          "data-test-id": "right-header-skeleton"
        })]
      }), renderedAdditionalContent, renderedBody]
    })
  });
}
CompactEngagementSkeleton.propTypes = {
  engagementType: PropTypes.string,
  heightOverride: PropTypes.number
};
export default CompactEngagementSkeleton;